import { useState, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for IDComply verification operations
 */
export const useIdComply = () => {
  const { toast } = useToast();
  const [isVerifying, setIsVerifying] = useState(false);

  /**
   * Performs data verification for a user
   * 
   * @returns Response with match result, or throws error with failedFields for field-specific errors
   */
  const performDataVerification = useCallback(async (profileData: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date | string;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phoneNumber?: string;
    email?: string;
    addressLine2?: string;
  }): Promise<{
    success: boolean;
    match?: 'full' | 'partial' | 'fail';
    details?: Array<{ field: string; match: string; message: string }>;
    failedFields?: Array<{ field: string; match: string; message: string }>;
    action?: 'success' | 'partial_success' | 'clear_fields' | 'clear_form';
    error?: string;
  }> => {
    setIsVerifying(true);
    try {
      const dateOfBirth = typeof profileData.dateOfBirth === 'string' 
        ? profileData.dateOfBirth 
        : profileData.dateOfBirth.toISOString().split('T')[0];

      const response = await apiClient.post('/idcomply/data-verification', {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        dateOfBirth,
        streetAddress: profileData.streetAddress,
        city: profileData.city,
        state: profileData.state,
        zip: profileData.zip,
        country: profileData.country,
        phoneNumber: profileData.phoneNumber,
        email: profileData.email,
      });

      if (!response.success) {
        // Get user-friendly error message from response
        const errorMessage = response.message || response.error || 'Data verification failed';
        const error: any = new Error(errorMessage);
        error.failedFields = response.failedFields || response.details;
        error.errorKey = response.errorKey;
        throw error;
      }

      return response;
    } catch (error: any) {
      console.error('Data verification error:', error);
      
      // Extract the most user-friendly error message available
      let errorMessage = 'Failed to verify your information. Please try again.';
      let failedFields = error.failedFields;
      
      if (error.response?.message || error.response?.data?.message) {
        errorMessage = (error.response?.message ?? error.response?.data?.message) as string;
      } else if (error.response?.error && typeof error.response.error === 'string') {
        // Convert error codes to user-friendly messages
        const errorCode = error.response.error;
        const errorMessages: Record<string, string> = {
          'VERIFICATION_FAILED': 'We couldn\'t verify your information. Please check your details and try again.',
          'INVALID_DATA': 'The information provided is invalid. Please check your details.',
          'SERVICE_UNAVAILABLE': 'The verification service is temporarily unavailable. Please try again later.',
          'MAX_ATTEMPTS_EXCEEDED': 'You have exceeded the maximum number of verification attempts. Please contact support.',
          'DUPLICATE_SUBMISSION': 'Please update the highlighted fields before trying again.',
          'INTERNAL_ERROR': 'An unexpected error occurred. Please try again later.',
        };
        errorMessage = errorMessages[errorCode] || errorCode;
        failedFields = error.response?.failedFields || error.response?.details;
      } else if (error.message && !error.message.startsWith('HTTP')) {
        errorMessage = error.message;
      }
      
      // Build a more helpful error message if we have failed fields
      let description = errorMessage;
      if (failedFields && Array.isArray(failedFields) && failedFields.length > 0) {
        const fieldNames = failedFields.map((f: any) => {
          const fieldMap: Record<string, string> = {
            'streetAddress': 'Address',
            'city': 'City',
            'state': 'State',
            'zip': 'ZIP Code',
            'dobYear': 'Birth Year',
            'dobMonth': 'Birth Month',
            'dobDay': 'Birth Day',
            'firstName': 'First Name',
            'lastName': 'Last Name',
            'email': 'Email',
            'phone': 'Phone',
          };
          return fieldMap[f.field] || f.field;
        });
        description = `Please check: ${fieldNames.join(', ')}`;
      }
      
      toast({
        title: 'Verification Error',
        description,
        variant: 'destructive',
        duration: 6000,
      });
      
      // Attach failedFields to the error so callers can use it
      error.failedFields = failedFields;
      throw error;
    } finally {
      setIsVerifying(false);
    }
  }, [toast]);

  /**
   * Creates a token for ID photo verification
   */
  const createIdVerificationToken = useCallback(async (redirectUrl?: string) => {
    setIsVerifying(true);
    try {
      const response = await apiClient.post('/idcomply/id-verification/token', {
        redirectUrl,
      });

      if (!response.success) {
        const errorMessage = response.message || response.error || 'Failed to create verification token';
        throw new Error(errorMessage);
      }

      return response;
    } catch (error: any) {
      console.error('ID verification token creation error:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Failed to create verification link. Please try again.';
      
      if (error.response?.message) {
        errorMessage = error.response.message;
      } else if (error.message && !error.message.startsWith('HTTP')) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Verification Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 6000,
      });
      throw error;
    } finally {
      setIsVerifying(false);
    }
  }, [toast]);

  /**
   * Gets the current verification status for the user
   */
  const getVerificationStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/idcomply/verification-status');
      return response;
    } catch (error: any) {
      console.error('Error getting verification status:', error);
      return null;
    }
  }, []);

  return {
    performDataVerification,
    createIdVerificationToken,
    getVerificationStatus,
    isVerifying,
  };
};

